/**
 * Cognito auth glue — thin wrapper around amazon-cognito-identity-js.
 *
 * Uses SRP auth (USER_SRP_AUTH) against the VTT app client. The pool
 * ID and client ID are baked in at build time via Vite env vars; the
 * app client must be configured as a public client (no secret) with
 * ALLOW_USER_SRP_AUTH and ALLOW_REFRESH_TOKEN_AUTH enabled.
 *
 * We expose:
 *   - signIn / signOut
 *   - getSessionState / getCurrentUser (session rehydration on page load)
 *   - getIdToken (attached to WebSocket $connect as ?token=...)
 *   - requestPasswordReset / confirmPasswordReset / resendConfirmationCode
 *
 * Username handling: the AdminUsers lambda invites players with their
 * email as the Cognito username. Players type that email back in here,
 * often from a phone keyboard that capitalises the first letter or
 * appends a space. We always trim, and if Cognito rejects the typed
 * form we retry once with the lowercased form — safe on both
 * case-insensitive (post-2020 default) and legacy case-sensitive pools.
 */
import {
  CognitoUser,
  CognitoUserPool,
  CognitoUserSession,
  AuthenticationDetails,
} from 'amazon-cognito-identity-js';
import { getAuthErrorCode, getAuthErrorMessage, normalizeEmail } from './authErrors';

const UserPoolId = import.meta.env.VITE_COGNITO_USER_POOL_ID as string;
const ClientId = import.meta.env.VITE_COGNITO_APP_CLIENT_ID as string;

if (!UserPoolId || !ClientId) {
  // Fail loud in dev; the login page would otherwise blow up later.
  // eslint-disable-next-line no-console
  console.error(
    'Missing VITE_COGNITO_USER_POOL_ID or VITE_COGNITO_APP_CLIENT_ID — check .env',
  );
}

export const userPool = new CognitoUserPool({
  UserPoolId,
  ClientId,
});

export interface AuthUser {
  sub: string;
  username: string;
  email: string;
  /** Cognito groups the user belongs to (from the cognito:groups claim). */
  groups: string[];
}

/** True if the user is a member of the "admins" Cognito group. */
export function isAdmin(user: AuthUser | null): boolean {
  return !!user && user.groups.includes('admins');
}

/**
 * Result of a sign-in attempt. The "newPasswordRequired" branch covers
 * the Cognito flow where an admin created the user with a temporary
 * password — the caller must collect a permanent password and call
 * <c>complete</c> to finish the challenge. The in-flight CognitoUser
 * is captured in the closure so completeNewPasswordChallenge has the
 * session state it needs.
 */
export type SignInResult =
  | { kind: 'success'; user: AuthUser }
  | {
      kind: 'newPasswordRequired';
      /** Attributes Cognito wants the user to fill in during first login. */
      requiredAttributes: string[];
      complete: (newPassword: string) => Promise<AuthUser>;
    };

function userFromSession(
  cognitoUser: CognitoUser,
  session: CognitoUserSession,
): AuthUser {
  const payload = session.getIdToken().payload as Record<string, unknown>;
  const rawGroups = payload['cognito:groups'];
  const groups = Array.isArray(rawGroups)
    ? rawGroups.map((g) => String(g))
    : typeof rawGroups === 'string' && rawGroups.length > 0
      ? [rawGroups]
      : [];
  return {
    sub: String(payload.sub ?? ''),
    username: String(
      payload['cognito:username'] ?? payload.preferred_username ?? cognitoUser.getUsername(),
    ),
    email: String(payload.email ?? ''),
    groups,
  };
}

// ---------------------------------------------------------------------
// Username normalisation
// ---------------------------------------------------------------------

/**
 * The forms of a typed username worth trying, in order: exactly as typed
 * (trimmed), then lowercased if that differs. Exported for tests.
 */
export function usernameCandidates(raw: string): string[] {
  const typed = raw.trim();
  const lowered = normalizeEmail(raw);
  return lowered === typed ? [typed] : [typed, lowered];
}

/**
 * Should a failure with the first candidate be retried with the next?
 * Only for "no such user" style failures — never for lockouts, expired
 * temporary passwords, or disabled accounts, where a retry would burn
 * another attempt for nothing. Exported for tests.
 */
export function isUsernameRetryable(err: unknown): boolean {
  const code = getAuthErrorCode(err);
  if (code === 'UserNotFoundException') return true;
  if (code === 'NotAuthorizedException') {
    // With "prevent user existence errors" enabled, an unknown username
    // comes back as a plain wrong-password error.
    return /incorrect username or password/i.test(getAuthErrorMessage(err));
  }
  return false;
}

async function withUsernameFallback<T>(
  raw: string,
  attempt: (username: string) => Promise<T>,
): Promise<T> {
  const candidates = usernameCandidates(raw);
  for (let i = 0; i < candidates.length; i++) {
    try {
      return await attempt(candidates[i]);
    } catch (err) {
      const last = i === candidates.length - 1;
      if (last || !isUsernameRetryable(err)) throw err;
    }
  }
  // Unreachable — the loop either returns or throws — but keeps TS happy.
  throw new Error('No username candidates');
}

// ---------------------------------------------------------------------
// Sign in / out
// ---------------------------------------------------------------------

function signInAs(username: string, password: string): Promise<SignInResult> {
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({ Username: username, Pool: userPool });
    const details = new AuthenticationDetails({ Username: username, Password: password });

    cognitoUser.authenticateUser(details, {
      onSuccess: (session) =>
        resolve({ kind: 'success', user: userFromSession(cognitoUser, session) }),
      onFailure: (err) => reject(err),
      newPasswordRequired: (userAttributes, requiredAttributes) => {
        // Cognito rejects these two on completeNewPasswordChallenge because
        // they aren't user-writable. Strip before we re-submit.
        delete userAttributes.email_verified;
        delete userAttributes.phone_number_verified;

        const complete = (newPassword: string) =>
          new Promise<AuthUser>((res, rej) => {
            cognitoUser.completeNewPasswordChallenge(newPassword, userAttributes, {
              onSuccess: (session) => res(userFromSession(cognitoUser, session)),
              onFailure: (err) => rej(err),
            });
          });

        resolve({
          kind: 'newPasswordRequired',
          requiredAttributes: Array.isArray(requiredAttributes) ? requiredAttributes : [],
          complete,
        });
      },
    });
  });
}

export function signIn(username: string, password: string): Promise<SignInResult> {
  return withUsernameFallback(username, (u) => signInAs(u, password));
}

export function signOut(): void {
  const cognitoUser = userPool.getCurrentUser();
  if (cognitoUser) {
    cognitoUser.signOut();
  }
}

// ---------------------------------------------------------------------
// Password recovery
// ---------------------------------------------------------------------

/**
 * Kick off the "forgot password" flow: tells Cognito to email a one-time
 * verification code to the address on the account. Resolves with the
 * username form Cognito accepted — pass that same string to
 * confirmPasswordReset so the two calls line up on a case-sensitive pool.
 *
 * Fails (NotAuthorizedException "cannot be reset in the current state")
 * for accounts that never completed first sign-in — see authErrors.ts.
 */
export function requestPasswordReset(email: string): Promise<string> {
  return withUsernameFallback(
    email,
    (username) =>
      new Promise<string>((resolve, reject) => {
        const cognitoUser = new CognitoUser({ Username: username, Pool: userPool });
        cognitoUser.forgotPassword({
          onSuccess: () => resolve(username),
          onFailure: (err) => reject(err),
        });
      }),
  );
}

/**
 * Complete the "forgot password" flow by submitting the code Cognito
 * sent plus the new password. On success the user still needs to sign
 * in again — we don't auto-log them in here to keep the flow explicit.
 */
export function confirmPasswordReset(
  username: string,
  code: string,
  newPassword: string,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({ Username: username.trim(), Pool: userPool });
    cognitoUser.confirmPassword(code, newPassword, {
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
    });
  });
}

/**
 * Re-send the sign-up confirmation code. Only relevant if an account is
 * somehow UNCONFIRMED (self sign-up isn't enabled, but a console-created
 * user can land there); admin-invited users never need this.
 */
export function resendConfirmationCode(email: string): Promise<void> {
  return withUsernameFallback(
    email,
    (username) =>
      new Promise<void>((resolve, reject) => {
        const cognitoUser = new CognitoUser({ Username: username, Pool: userPool });
        cognitoUser.resendConfirmationCode((err) => (err ? reject(err) : resolve()));
      }),
  );
}

// ---------------------------------------------------------------------
// Session rehydration
// ---------------------------------------------------------------------

/**
 * What the browser's cached Cognito session looks like right now.
 *
 *  - signedIn: valid tokens (refreshed silently if the ID token was stale).
 *  - none:     nobody cached — a fresh browser, or after an explicit sign-out.
 *  - expired:  cached tokens exist but the refresh token is no longer
 *              accepted (past the pool's refresh-token validity, revoked,
 *              or the user was deleted/disabled). The library has already
 *              cleared the cache in the NotAuthorized case.
 *  - offline:  cached tokens exist but the refresh call couldn't reach
 *              Cognito. The cache is intact — a retry may succeed.
 */
export type SessionState =
  | { status: 'signedIn'; user: AuthUser }
  | { status: 'none' }
  | { status: 'expired'; error: unknown }
  | { status: 'offline'; error: unknown };

export function getSessionState(): Promise<SessionState> {
  return new Promise((resolve) => {
    const cognitoUser = userPool.getCurrentUser();
    if (!cognitoUser) {
      resolve({ status: 'none' });
      return;
    }
    cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err) {
        const code = getAuthErrorCode(err);
        if (code === 'NetworkError') {
          resolve({ status: 'offline', error: err });
        } else if (code === 'NoSession') {
          resolve({ status: 'none' });
        } else {
          resolve({ status: 'expired', error: err });
        }
        return;
      }
      if (!session || !session.isValid()) {
        resolve({ status: 'expired', error: new Error('Cached session is not valid') });
        return;
      }
      resolve({ status: 'signedIn', user: userFromSession(cognitoUser, session) });
    });
  });
}

/**
 * Rehydrate session from the browser's Cognito storage. Returns null if
 * there's no user cached or the refresh token has expired. Prefer
 * getSessionState when you need to know *why* there's no user.
 */
export async function getCurrentUser(): Promise<AuthUser | null> {
  const state = await getSessionState();
  return state.status === 'signedIn' ? state.user : null;
}

/**
 * Get a currently valid ID token, refreshing silently if the cached
 * one is stale. This is what we pass to API Gateway WebSocket on
 * $connect — the Cognito authorizer validates its signature server-side.
 */
export function getIdToken(): Promise<string | null> {
  return new Promise((resolve) => {
    const cognitoUser = userPool.getCurrentUser();
    if (!cognitoUser) {
      resolve(null);
      return;
    }
    cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session || !session.isValid()) {
        resolve(null);
        return;
      }
      resolve(session.getIdToken().getJwtToken());
    });
  });
}
